import { Text, View, StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { CtaRfos } from "./rfos";
import AlertsTable from "./alerts";
import { GraphViewer } from "../utils/graph";
import { RangeView } from "../firstPageComponents/range";
import { PdfTable } from "./pdfTable";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("pt-br");
const TIMEZONE = "America/Sao_Paulo";

const formatDuration = (milliseconds) => {
	if (!milliseconds) return "-";
	const totalSeconds = Math.floor(milliseconds / 1000);
	const days = Math.floor(totalSeconds / (3600 * 24));
	const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const parts = [];
	if (days > 0) parts.push(`${days}d`);
	if (hours > 0) parts.push(`${hours}h`);
	if (minutes > 0) parts.push(`${minutes}m`);
	if (seconds > 0) parts.push(`${seconds}s`);

	return parts.length > 0 ? parts.join(" ") : "0s";
};

export const CtaReport = ({
	relatorio,
	sharedOptions,
	rfos,
	totalTickets,
	graphcs,
}) => {
	const quantidadeSomadaRfos = Object.values(rfos || {}).reduce(
		(acc, listaAtual) => {
			return acc + (listaAtual?.length || 0);
		},
		0,
	);

	const totalGeralAtendimentos = totalTickets + quantidadeSomadaRfos;

	const totalMinutes =
		dayjs(relatorio?.dataFinal).isValid() &&
			dayjs(relatorio?.dataInicial).isValid()
			? dayjs(relatorio?.dataFinal).diff(
				dayjs(relatorio?.dataInicial),
				"minute",
			)
			: 0;

	// Pre-compute excess values for mutual exclusion
	let availExcessMinutes = 0;
	let unavailExcessMinutes = 0;

	if (relatorio?.metricsTable?.values?.availability !== undefined) {
		const aMin = Math.round((totalMinutes * relatorio.metricsTable.values.availability) / 100);
		const aRef = Math.round((totalMinutes * relatorio.metricsTable.ref.availability) / 100);
		availExcessMinutes = aMin - aRef;
	}
	if (relatorio?.metricsTable?.values?.unavailability !== undefined) {
		const uMin = Math.round((totalMinutes * relatorio.metricsTable.values.unavailability) / 100);
		const uRef = Math.round((totalMinutes * relatorio.metricsTable.ref.unavailability) / 100);
		unavailExcessMinutes = uMin - uRef;
	}

	const metricsData = [];

	if (relatorio?.metricsTable?.values?.availability !== undefined) {
		const availValue = relatorio.metricsTable.values.availability;
		const availRef = relatorio.metricsTable.ref.availability;
		const availMinutes = Math.round((totalMinutes * availValue) / 100);
		const refMinutes = Math.round((totalMinutes * availRef) / 100);

		metricsData.push({
			label: "Disponibilidade de Coleta de Ping",
			resultPercent: `${availValue.toFixed(2)} %`,
			resultMin: `${availMinutes} min`,
			refValue: `${availValue >= availRef ? ">=" : "<"} ${availRef} %`,
			refMin: `${refMinutes} min`,
			excess: availExcessMinutes > 0 && unavailExcessMinutes <= 0 ? `${availExcessMinutes} min` : "-",
		});
	}

	if (relatorio?.metricsTable?.values?.unavailability !== undefined) {
		const unavailValue = relatorio.metricsTable.values.unavailability;
		const unavailRef = relatorio.metricsTable.ref.unavailability;
		const unavailMinutes = Math.round((totalMinutes * unavailValue) / 100);
		const refUnavailMinutes = Math.round((totalMinutes * unavailRef) / 100);

		metricsData.push({
			label: "Indisponibilidade",
			resultPercent: `${unavailValue.toFixed(2)} %`,
			resultMin: `${unavailMinutes} min`,
			refValue: `${unavailValue <= unavailRef ? "<=" : ">"
				} ${unavailRef % 1 === 0 ? unavailRef : unavailRef.toFixed(2)} %`,
			refMin: `${refUnavailMinutes} min`,
			excess: unavailExcessMinutes > 0 && availExcessMinutes <= 0 ? `${unavailExcessMinutes} min` : "-",
		});
	}

	if (relatorio?.metricsTable?.values?.latency !== undefined) {
		metricsData.push({
			label: "Latência Média (ms)",
			resultPercent: `${relatorio.metricsTable.values.latency.toFixed(2)} ms`,
			resultMin: "-",
			refValue: `${relatorio.metricsTable.values.latency >
				relatorio.metricsTable.ref.latency
				? ">"
				: "<="
				} ${relatorio.metricsTable.ref.latency} ms`,
			refMin: "-",
			excess: "-",
		});
	}

	if (relatorio?.metricsTable?.values?.packetLoss !== undefined) {
		metricsData.push({
			label: "Perda de Pacotes (%)",
			resultPercent: `${relatorio.metricsTable.values.packetLoss.toFixed(2)} %`,
			resultMin: "-",
			refValue: `${relatorio.metricsTable.values.packetLoss >
				relatorio.metricsTable.ref.packetLoss
				? ">"
				: "<="
				} ${relatorio.metricsTable.ref.packetLoss} %`,
			refMin: "-",
			excess: "-",
		});
	}

	const metricsColumns = [
		{ header: "Métrica", width: "22%", dataKey: "label" },
		{ header: "Resultado", width: "15%", dataKey: "resultPercent" },
		{ header: "Tempo", width: "13%", dataKey: "resultMin" },
		{ header: "Valor Referência", width: "19%", dataKey: "refValue" },
		{ header: "Tempo Referência", width: "18%", dataKey: "refMin" },
		{ header: "Excedente", width: "13%", dataKey: "excess" },
	];

	const alertsColumns = [
		{ header: "Alerta", width: "48%", dataKey: "alert" },
		{
			header: "Horário Ocorrência",
			width: "21%",
			render: (row) => dayjs.unix(row.initialDate).format("DD/MM/YYYY HH:mm"),
		},
		{
			header: "Horário Recuperação",
			width: "21%",
			render: (row) =>
				row.recoveryDate
					? dayjs.unix(row.recoveryDate).format("DD/MM/YYYY HH:mm")
					: "-",
		},
		{
			header: "Duração",
			width: "10%",
			render: (row) => formatDuration(row.duration),
		},
	];

	return (
		<View style={styles.firsPageContainer}>
			<RangeView sharedOptions={sharedOptions} />
			<View style={{ display: "flex", flexDirection: "column", gap: 1 }}>
				<Text
					style={{
						fontSize: 14,
						textAlign: "left",
						fontFamily: "Times-Bold",
						color: "#212B36",
						marginBottom: 5,
					}}
				>
					{`Cliente : ${relatorio?.client?.toUpperCase()}`}
				</Text>
				<View
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
					}}
				>
					<Text
						style={{
							fontSize: 12,
							textAlign: "justify",
							fontFamily: "Times-Roman",
							lineHeight: 1.5,
							textIndent: "20px",
						}}
					>
						{`Este documento apresenta os indicadores de disponibilidade e o registro de incidentes dos ativos monitorados para o cliente: ${relatorio?.client}. As coletas dos valores apresentados neste relatório correspondem ao período entre ${sharedOptions?.dataInicial} ${sharedOptions?.horaInicial} à ${sharedOptions?.dataFinal} ${sharedOptions?.horaFinal}. As métricas são baseadas no tempo total do período e na soma das interrupções registradas, visando validar o cumprimento dos níveis de serviço (SLA) acordados.`}
					</Text>
				</View>
			</View>

			<Text style={styles.countDescription}>
				{`Total de Alertas no Período: ${relatorio?.alerts?.length}`}
			</Text>
			<Text style={styles.countDescription}>
				{`Total de Atendimentos no Período: ${totalGeralAtendimentos}`}
			</Text>

			<View style={{ height: 10 }} />

			<View style={styles.graphContainer}>
				<GraphViewer
					graphcs={graphcs?.availability}
					sharedOptions={sharedOptions}
					description={`Disponibilidade`}
					font={`Fonte: Dados coletados pelo ${relatorio?.source}`}
					options={{
						header: {},
						container: { width: "230px", marginTop: "-5px" },
						footer: {
							marginTop: "-5px",
						},
					}}
				/>

				<Text
					style={{
						...styles.countDescription,
						textIndent: "20px",
						marginBottom: 10,
					}}
				>
					O gráfico a seguir mostra a disponibilidade de coleta de ping nas
					comunicações ICMP com os equipamentos monitorados ao longo do tempo.
				</Text>
				<GraphViewer
					graphcs={graphcs?.graphPingLine}
					sharedOptions={sharedOptions}
					description={`Tempo de Resposta`}
					font={`Fonte: Dados coletados pelo ${relatorio?.source}`}
					options={{
						header: {},
						container: {},
						footer: {},
					}}
				/>
			</View>

			{graphcs?.graphLatency && (
				<View style={styles.metricsInColumn} break>
					<Text
						style={{
							...styles.countDescription,
							textIndent: "20px",
							marginBottom: 10,
						}}
					>
						O gráfico a seguir mostra a variação da latência (tempo de resposta)
						nas comunicações ICMP com os equipamentos monitorados ao longo do
						tempo.
					</Text>
					<GraphViewer
						graphcs={graphcs?.graphLatency}
						sharedOptions={sharedOptions}
						description={`Latência`}
						font={`Fonte: Dados coletados pelo ${relatorio?.source}`}
						options={{
							header: {},
							container: {},
							footer: {},
						}}
					/>
				</View>
			)}

			<PdfTable columns={metricsColumns} data={metricsData} />

			<PdfTable
				title="Alertas:"
				columns={alertsColumns}
				data={relatorio?.alerts}
			/>

			<View style={{ height: 15 }} />
			{totalGeralAtendimentos > 0 ? (
				<CtaRfos
					rfos={rfos}
					totalTickets={totalTickets}
					relatorio={relatorio}
				/>
			) : (
				<View
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Text
						style={{ ...styles.countDescription, fontSize: 14, marginTop: 10 }}
					>
						Sem Atendimentos neste período
					</Text>
				</View>
			)}
			{relatorio?.observacoes && (
				<View
					style={{
						...styles.metricsInColumn,
						marginTop: 10,
						padding: 10,
						backgroundColor: "#f5f5f5",
						borderRadius: 4,
						width: "100%",
					}}
					wrap
				>
					<Text
						style={{
							fontSize: 12,
							fontFamily: "Times-Bold",
							marginBottom: 5,
							color: "#000000",
						}}
					>
						Observações:
					</Text>

					<Text
						style={{
							fontSize: 12,
							textAlign: "justify",
							fontFamily: "Times-Roman",
							lineHeight: 1.5,
							width: "100%",
						}}
					>
						{relatorio.observacoes}
					</Text>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	firsPageContainer: { display: "flex", flexDirection: "column" },
	countDescription: {
		fontSize: 12,
		textAlign: "justify",
		fontFamily: "Times-Roman",
		lineHeight: 1.5,
	},
	graphContainer: {
		width: "100%",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: -10,
		gap: 10,
	},
	metricsInColumn: {
		display: "flex",
		flexDirection: "column",
		marginTop: 15,
		marginBottom: 15,
		width: "100%",
	},

	colMetric: { width: "35%" },
	colResult: { width: "20%" },
	colResultMin: { width: "20%" },
	colRef: { width: "25%" },
});
